<!-- Usage:
<CircleProgressBar style="width: 200px; height: 200px"
progressSegments: [
        { percentage: 10, color: '#498500' },
        { percentage: 30, color: '#484848' },
        { percentage: 30, color: '#a2a09d' }
      ]
progressSubText="IN PROGRESS"
:showImage="true"
 /> -->

<template>
  <div class="circle-progress" @click="emitProgressBarClick()">
    <!-- Image on top-left -->
    <img
      v-if="showImage"
      src="../../assets/icons/paper-airplane.svg"
      alt="progress bar paper plane"
      class="progress-bar-image"
    />

    <!-- SVG Circle for the Progress Bar -->
    <svg viewBox="0 0 120 120" class="circle-svg">
      <!-- Grey Background Circle -->
      <circle class="circle-bg-grey" cx="60" cy="60" r="50" />

      <circle v-for="(dot, index) in dots" :key="`dot-${index}`" class="circle-dot" :cx="dot.x" :cy="dot.y" r="5" />

      <!-- Progress Circles (Rendered in Reverse Order for Correct Stacking) -->
      <g
        v-for="(progress, index) in progressSegments.slice().reverse()"
        :key="progressSegments.length - index - 1"
        class="progress-segment"
        @mouseenter="((currentHover = progressSegments.length - index - 1), showTooltip(progress, $event))"
        @mouseleave="((currentHover = null), hideTooltip())"
        @mousemove="updateTooltipPosition"
      >
        <!-- Hover Background Circle -->
        <circle
          class="circle-progress-hover"
          cx="60"
          cy="60"
          r="50"
          v-if="currentHover === progressSegments.length - index - 1 && showHover"
          :style="[
            getProgressStyle(progress.percentage, getStartOffset(progressSegments.length - index - 1), progress.color),
            { opacity: 0.5 }
          ]"
        />
        <!-- Original Progress Circle -->
        <circle
          class="circle-progress"
          cx="60"
          cy="60"
          r="50"
          :style="
            progress.percentage
              ? [
                  getProgressStyle(
                    progress.percentage,
                    getStartOffset(progressSegments.length - index - 1),
                    showHover ? progress.color : '#498500'
                  )
                ]
              : ''
          "
        />
      </g>
    </svg>

    <!-- Progress bar text -->
    <div class="circle-center">
      <div :class="progressBarTextClass" :style="progressBarTextStyle">{{ progressText }}</div>
      <div v-if="progressSubText" class="progress-bar-subtext">{{ progressSubText }}</div>
    </div>

    <!-- Tooltip -->
    <div
      v-if="tooltip.show && showHover"
      class="tooltip px-2 py-1"
      :style="{ top: `${tooltip.y}px`, left: `${tooltip.x}px` }"
    >
      {{ tooltip.content }}
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';

export default defineComponent({
  emits: ['progressBarClick'],
  name: 'CircleProgressBar',

  props: {
    progressSegments: {
      type: Array,
      required: true,
      default: () => []
    },
    progressSubText: {
      type: String,
      default: ''
    },
    progressText: {
      type: String,
      default: '0'
    },
    showImage: {
      type: Boolean,
      default: false
    },
    showHover: {
      type: Boolean,
      default: false
    },
    progressBarTextClass: {
      type: String,
      default: 'progress-bar-text'
    },
    progressBarTextStyle: {
      type: String,
      default: ''
    },
    dotPositions: {
      type: Array,
      default: () => [0, 20, 40, 60, 80, 100]
    }
  },

  data() {
    return {
      currentHover: null, // Tracks the currently hovered segment
      tooltip: {
        show: false,
        content: '',
        x: 0,
        y: 0
      }
    };
  },

  computed: {
    dots() {
      const positions = this.dotPositions; // Percent positions for the dots
      const radius = 50;
      const centerX = 60;
      const centerY = 60;

      return positions.map((percent) => {
        const angle = (percent / 100) * 2 * Math.PI - Math.PI / 2; // Convert percentage to angle
        return {
          x: centerX + radius * Math.cos(angle),
          y: centerY + radius * Math.sin(angle)
        };
      });
    },
    computedProgressSegments() {
      if (!this.showHover) {
        // Merge all progress into a single green color
        const totalProgress = this.progressSegments.reduce((sum, segment) => sum + segment.percentage, 0);
        return [{ percentage: totalProgress, color: '#498500' }];
      }
      // Return original segments when showHover is true
      return this.progressSegments;
    }
  },

  methods: {
    getStartOffset(index) {
      return this.progressSegments.slice(0, index).reduce((total, segment) => total + segment.percentage, 0);
    },
    getProgressStyle(progress, startOffset, color) {
      const circumference = 2 * Math.PI * 50; // Total circumference of the circle
      const startPercent = startOffset / 100;
      const endPercent = (startOffset + progress) / 100;

      const visibleLength = (endPercent - startPercent) * circumference;
      const dashOffset = circumference * (1 - startPercent);

      return {
        stroke: color || '#498500',
        strokeDasharray: `${visibleLength} ${circumference - visibleLength}`,
        strokeDashoffset: dashOffset
      };
    },
    showTooltip(progress, event) {
      this.tooltip.show = true;
      this.tooltip.content = `${progress.percentage}%`;
      this.updateTooltipPosition(event);
    },
    hideTooltip() {
      this.tooltip.show = false;
    },
    updateTooltipPosition(event) {
      this.tooltip.x = event.clientX + 10; // Offset by 10px to the right
      this.tooltip.y = event.clientY + 10; // Offset by 10px below
    },
    emitProgressBarClick() {
      this.$emit('progressBarClick');
    }
  }
});
</script>

<style lang="scss" scoped>
/* Overall container */
.circle-progress {
  position: relative;
  width: 100%;
  height: 100%;
}

/* SVG styles */
.circle-svg {
  transform: rotate(-90deg);
  width: 100%;
  height: 100%;
}

.circle-dot {
  fill: #fff;
}

/* Grey Background Circle */
.circle-bg-grey {
  fill: none;
  stroke: #e2e2e2;
  stroke-width: 17;
  stroke-dasharray: 314;
  stroke-dashoffset: 0;
}

/* Progress Circles */
.circle-progress {
  fill: none;
  stroke-width: 17;
  stroke-linecap: round;
  stroke-dasharray: 314;
  stroke-dashoffset: 314;
  transition: stroke-dashoffset 0.3s ease-in-out;
}

/* Hover Background Circle */
.circle-progress-hover {
  fill: none;
  stroke-width: 20;
  transition: stroke-width 0.3s ease-in-out;
}

/* Center text styles */
.circle-center {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}

.progress-bar-text {
  font-size: 24px;
  font-weight: bold;
  color: #498500;
  @media (min-width: 768px) {
    font-size: 32px;
  }
}

.progress-bar-subtext {
  font-size: 12px;
  color: #666;
}

/* Top-left image */
.progress-bar-image {
  position: absolute;
  top: -10px;
  left: -10px;
  width: 30px;
  height: 30px;
  object-fit: contain;
}

/* Tooltip styles */
.tooltip {
  position: fixed;
  background: #e8e8e8;
  color: #000000;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  pointer-events: none;
  white-space: nowrap;
  z-index: 1000;
  opacity: 90%;
}
</style>
